/**
 * Converts a potentially nested data structure to tabular data. For one-to-many relationships, the single-valued column will
 * be expanded and its value duplicated in the same number of rows multi-valued column has. Unrelated columns will just
 * be generated as they are with missing values padded with empty strings.
 *
 * @param columns the flattened columns
 * @param data the assembled data
 */
const tabularize = ({columns, data}) => {
    let result = []

    // For each item in the assembled data set
    data.forEach(item => {
        const rowsFromItem = []

        /* First, create a map from the item's values in such a way that every value is an array holding the values in the rows.
           The sizes of these array may differ, so we'll apply some value padding next. */
        const columnData = mapItemToColumnData(item)

        // Get the maximum number of rows in the column data
        const maxLen = getColumnDataMaxLen(columnData)

        for (let i=0; i<maxLen; i++) {
            const row = {}

            // For each column to be filled
            columns.forEach(col => {
                // If its value is missing from the column data, pad it with empty string
                if (!columnData[col]) {
                    row[col] = ''
                }
                // If the column data has a single value in this column
                else if (columnData[col].length === 1) {
                    row[col] = columnData[col][0]
                }
                /* Otherwise, just include the column's value in the row, if it exist with the current index
                   (or else pad with an empty string) */
                else if (i < columnData[col].length) {
                    row[col] = columnData[col][i]
                } else {
                    row[col] = ''
                }
            })
            rowsFromItem.push(row)
        }

        result = result.concat(rowsFromItem)
    })

    return result
}

const addValueToColumnData = ({value, column, result}) => {
    if (!result[column]) {
        result[column] = []
    }
    result[column].push(value)
}

const mapValueToFieldRec = ({value, columnPrefix, field, result}) => {
    const fullColumnName = !columnPrefix ? field : `${columnPrefix}.${field}`

    // If it's a primitive type, add to the map
    if (typeof value !== 'object') {
        addValueToColumnData({value, column: fullColumnName, result})
    }
    // If it's an array, apply this algorithm recursively to each of its elements
    else if (Array.isArray(value)) {
        value.forEach(elem => mapValueToFieldRec({value: elem, columnPrefix, field, result}))
    }
    // If it's a single object, apply this algorithm recursively to each of its fields
    else {
        Object.keys(value).forEach(subField => mapValueToFieldRec({value: value[subField], columnPrefix: fullColumnName, field: subField, result}))
    }
}

const mapItemToColumnData = (item) => {
    const result = {}
    Object.keys(item).forEach(field => mapValueToFieldRec({value: item[field], columnPrefix: '', field, result}))
    return result
}

const getColumnDataMaxLen = (columnData) => {
    return Math.max(...Object.keys(columnData).map(col => columnData[col].length))
}


module.exports = {tabularize}